import React, { useState, useRef, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  Drawer,
  Button,
  DatePicker,
  Checkbox,
  PopupMenu,
  Loader,
  TextInputWithLabel
} from 'quantux-ui';
import { gray, blue, salmon } from '../../theme';
import { BASE_URL, padZero } from '../../utils';

// --- Constants ---
const TODAY = new Date();

// --- ICONS ---
const idIcon = (
  <svg
    width="20"
    height="15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.406 0h17.188C19.37 0 20 .629 20 1.406v12.188C20 14.37 19.371 15 18.594 15H1.406A1.405 1.405 0 0 1 0 13.594V1.406C0 .63.629 0 1.406 0ZM8.75 11.094c0 .777-.629 1.406-1.406 1.406H3.906A1.405 1.405 0 0 1 2.5 11.094V9.375c0-1.078.545-2.027 1.375-2.59a2.5 2.5 0 1 1 3.5 0 3.122 3.122 0 0 1 1.375 2.59v1.719ZM6.719 5a1.094 1.094 0 1 0-2.19.001A1.094 1.094 0 0 0 6.72 5ZM3.906 9.473c0-.006 0-.012.002-.02-.002-.025-.002-.053-.002-.078a1.719 1.719 0 0 1 3.438 0c0 .027 0 .053-.002.078.002.006.002.012.002.02v1.523a.097.097 0 0 1-.098.098H4.004a.097.097 0 0 1-.098-.098V9.473Zm14.688 4.12H1.406V1.407h17.188v12.188Zm-3.047-5.39h-4.844a.703.703 0 1 1 0-1.406h4.844a.703.703 0 1 1 0 1.406Zm-4.844 3.203h3.594a.703.703 0 1 0 0-1.406h-3.594a.703.703 0 1 0 0 1.406ZM16.797 5h-6.094a.703.703 0 1 1 0-1.406h6.094a.703.703 0 1 1 0 1.406Z"
      fill="#9CF"
    />
  </svg>
);

const PatientProfileDrawer = (props) => {
  const [canUpdatePatient, setCanUpdatePatient] = useState(false);
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientMiddleName, setPatientMiddleName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientSSN1, setPatientSSN1] = useState('');
  const [patientSSN2, setPatientSSN2] = useState('');
  const [patientSSN3, setPatientSSN3] = useState('');
  const [patientDobDate, setPatientDobDate] = useState(TODAY.getDate());
  const [patientDobMonth, setPatientDobMonth] = useState(TODAY.getMonth());
  const [patientDobYear, setPatientDobYear] = useState(TODAY.getFullYear());
  const [isPatientDobPristine, setIsPatientDobPristine] = useState(true);
  const [showPatientDobAlert, setShowPatientDobAlert] = useState(true);
  const [patientAvatarImageUrl, setPatientAvatarImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg');
  const [patientDisplayName, setPatientDisplayName] = useState('');
  const [isPatientDisplayNameValid, setIsPatientDisplayNameValid] = useState(false);
  const [isPatientDisplayNamePristine, setIsPatientDisplayNamePristine] = useState(true);
  const [patientUserName, setPatientUserName] = useState('');
  const [isPatientUserNameValid, setIsPatientUserNameValid] = useState(false);
  const [isPatientUserNamePristine, setIsPatientUserNamePristine] = useState(true);
  const [isPatientUserNameDuplicate, setIsPatientUserNameDuplicate] = useState(true);
  // -- Home Address
  const [patientHomeStreetAddress, setPatientHomeStreetAddress] = useState('');
  const [patientHomeSuite, setPatientHomeSuite] = useState('');
  const [patientHomeCity, setPatientHomeCity] = useState('');
  const [patientHomeState, setPatientHomeState] = useState('');
  const [patientHomeZip, setPatientHomeZip] = useState('');
  // -- Mailing Address
  const [isPatientMailingAddressSameAsHomeAddress, setIsPatientMailingAddressSameAsHomeAddress] = useState(false);
  const [patientMailingStreetAddress, setPatientMailingStreetAddress] = useState('');
  const [patientMailingSuite, setPatientMailingSuite] = useState('');
  const [patientMailingCity, setPatientMailingCity] = useState('');
  const [patientMailingState, setPatientMailingState] = useState('');
  const [patientMailingZip, setPatientMailingZip] = useState('');
  // -- Contact (Mobile/Phone)
  const [patientMobile, setPatientMobile] = useState('');
  const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
  const [patientContactType, setPatientContactType] = useState('mobile');
  // -- Email
  const [patientEmail, setPatientEmail] = useState('');
  // -- Patient ID (FRONT)
  const [showFrontIdLoader, setShowFrontIdLoader] = useState(false);
  const [patientFrontIdImageUrl, setPatientFrontIdImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  // -- Patient ID (BACK)
  const [showBackIdLoader, setShowBackIdLoader] = useState(false);
  const [patientBackIdImageUrl, setPatientBackIdImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');

  const {
    isVisible,
    patientData,
    placement,
    style,
    onClose,
    onSavePatientProfile,
    onClickAvatarCamera,
    onClickFrontIdCamera,
    onClickBackIdCamera
  } = props;

  const {
    avatar = 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
    contactType = 'mobile',
    displayName = '',
    dob,
    email = '',
    firstName = '',
    middleName = '',
    lastName = '',
    gender = '',
    homeAddress = {},
    mailingAddress = {},
    mailingAddressSameAsHomeAddress = false,
    mobile = '',
    ssn1 = '',
    ssn2 = '',
    ssn3 = '',
    userName = '',
    patientIdFrontScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg',
    patientIdBackScan = 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'
  } = patientData;

  const {
    streetAddress1: homeStreetAddress1 = '',
    suite: homeSuite = '',
    city: homeCity = '',
    state: homeState = '',
    zip: homeZip = ''
  } = homeAddress;

  const {
    streetAddress1: mailingStreetAddress1 = '',
    suite: mailingSuite = '',
    city: mailingCity = '',
    state: mailingState = '',
    zip: mailingZip = ''
  } = mailingAddress;

  // ------------------- Refs -------------------
  const isFirstRender = useRef(true);
  const drawerAvatarRef = useRef();
  const contactRef = useRef();
  const frontCardRef = useRef();
  const backCardRef = useRef();

  // ------------------- Styles -------------------
  const styles = StyleSheet.create({
    sideDrawerContainer: {
      // background: gray[600],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    sideDrawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    sideDrawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerContentArea: {
      // height: 'calc(100vh - 186px)',
      // height: `${window.innerHeight - 170}px`,
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    sideDrawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    sideDrawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerUserCardText: {
      fontSize: '12px'
    },
    sideDrawerFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexShrink: '0',
      position: 'absolute',
      bottom: '0',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      paddingRight: '8px'
    },
    sectionHeading: {
      color: blue[200],
      fontSize: '14px',
      fontWeight: 'bold'
    },
    ssnSeparator: {
      padding: '16px 4px 0 4px'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '4px',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    menuItemIconContainerSmall: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '25px',
      width: '25px'
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '40px',
      width: '40px'
    },
    menuItemIconSmall: {
      fontSize: '14px'
    },
    menuItemIcon: {
      fontSize: '20px'
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    popupMenuItemSmall: {
      height: '25px',
      display: 'flex',
      fontSize: '14px',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#5080B0',
      ':not(.selected):hover': {
        backgroundColor: '#0066CC',
        color: 'white'
      }
    },
    selectedPopupMenuItemSmall: {
      backgroundColor: '#0066CC',
      color: 'white'
    }
  });

  // ------------------- Effects -------------------

  // Initial Patient Data
  useEffect(() => {
    if (isFirstRender.current) {
      setPatientAvatarImageUrl(avatar);
      setPatientUserName(userName);
      setPatientDisplayName(displayName);
      setPatientFirstName(firstName);
      setPatientMiddleName(middleName);
      setPatientLastName(lastName);
      setPatientSSN1(ssn1);
      setPatientSSN2(ssn2);
      setPatientSSN3(ssn3);
      // DOB
      const tmp1 = dob.split('T');
      const tmp2 = tmp1[0].split('-');
      const _year = tmp2[0];
      const _month = tmp2[1];
      const _date = tmp2[2];
      setPatientDobDate(+_date);
      setPatientDobMonth(+(_month) - 1);
      setPatientDobYear(+_year);
      setShowPatientDobAlert(false);
      setIsPatientDobPristine(false);
      // Home address
      // setPatientHomeStreetAddress(homeAddress.streetAddress1 || '');
      // setPatientHomeSuite(homeAddress.suite || '');
      // setPatientHomeCity(homeAddress.city || '');
      // setPatientHomeState(homeAddress.state || '');
      // setPatientHomeZip(homeAddress.zip || '');
      setPatientHomeStreetAddress(homeStreetAddress1);
      setPatientHomeSuite(homeSuite);
      setPatientHomeCity(homeCity);
      setPatientHomeState(homeState);
      setPatientHomeZip(homeZip);
      // Mailing Address
      setIsPatientMailingAddressSameAsHomeAddress(mailingAddressSameAsHomeAddress);
      if (!mailingAddressSameAsHomeAddress) {
        // setPatientMailingStreetAddress(mailingAddress.streetAddress1);
        // setPatientMailingSuite(mailingAddress.suite);
        // setPatientMailingCity(mailingAddress.city);
        // setPatientMailingState(mailingAddress.state);
        // setPatientMailingZip(mailingAddress.zip);
        setPatientMailingStreetAddress(mailingStreetAddress1);
        setPatientMailingSuite(mailingSuite);
        setPatientMailingCity(mailingCity);
        setPatientMailingState(mailingState);
        setPatientMailingZip(mailingZip);
      } else {
        // setPatientMailingStreetAddress(homeAddress.streetAddress1);
        // setPatientMailingSuite(homeAddress.suite);
        // setPatientMailingCity(homeAddress.city);
        // setPatientMailingState(homeAddress.state);
        // setPatientMailingZip(homeAddress.zip);
        setPatientMailingStreetAddress(homeStreetAddress1);
        setPatientMailingSuite(homeSuite);
        setPatientMailingCity(homeCity);
        setPatientMailingState(homeState);
        setPatientMailingZip(homeZip);
      }
      setPatientContactType(contactType);
      setPatientMobile(mobile);
      setPatientEmail(email);
      setPatientFrontIdImageUrl(patientIdFrontScan);
      setPatientBackIdImageUrl(patientIdBackScan);
      isFirstRender.current = false;
    }
  }, [
    avatar,
    contactType,
    displayName,
    dob,
    email,
    firstName,
    middleName,
    lastName,
    gender,
    homeAddress,
    mailingAddress,
    mailingAddressSameAsHomeAddress,
    mobile,
    ssn1,
    ssn2,
    ssn3,
    userName,
    patientIdFrontScan,
    patientIdBackScan
  ]);
  // Duplicate Username
  useEffect(() => {
    if (patientUserName === userName) {
      setIsPatientUserNameDuplicate(false);
      console.log('isPatientUserNameDuplicate = false');
    } else if (patientUserName.length >= 2 && patientUserName.length <= 20 && patientUserName.indexOf(' ') < 0) {
      const url = new URL(`${BASE_URL}/patients/checkDuplicateUserName`);
      const params = { userName: patientUserName };
      url.search = new URLSearchParams(params).toString();

      fetch(url)
        .then(response => response.text())
        .then(result => {
          const reqData = JSON.parse(result);
          if (reqData.success) {
            setIsPatientUserNameDuplicate(reqData.isDuplicate);
            console.log(`isPatientUserNameDuplicate = ${reqData.isDuplicate}`);
          } else {
            setIsPatientUserNameDuplicate(true);
            console.log('isPatientUserNameDuplicate = true');
          }
        })
        .catch(error => console.log('error', error));
    }
  }, [patientUserName]);

  // Reset Mailing Address when mailing addresset set to not same as home address
  useEffect(() => {
    if (isPatientMailingAddressSameAsHomeAddress) {
      setPatientMailingStreetAddress('');
      setPatientMailingSuite('');
      setPatientMailingCity('');
      setPatientMailingState('');
      setPatientMailingZip('');
    }
  }, [isPatientMailingAddressSameAsHomeAddress]);

  // Can Update Patient Flag
  useEffect(() => {
    const patientDisplayNameFlag = patientDisplayName.length >= 2 && patientDisplayName.length <= 40;
    const patientUserNameFlag = patientUserName.length >= 2 && patientUserName.length <= 20 && patientUserName.indexOf(' ') < 0 && !isPatientUserNameDuplicate;
    setCanUpdatePatient(patientDisplayNameFlag && patientUserNameFlag);
  }, [
    patientDisplayName,
    isPatientDisplayNamePristine,
    isPatientDisplayNameValid,
    patientUserName,
    isPatientUserNamePristine,
    isPatientUserNameDuplicate,
    isPatientUserNameValid
  ]);

  // #region Event Handlers
  const handleOnEditPatientFirstName = (e) => {
    const { value } = e.target;
    setPatientFirstName(value);
  };

  const handleOnEditPatientMiddleName = (e) => {
    const { value } = e.target;
    setPatientMiddleName(value);
  };

  const handleOnEditPatientLastName = (e) => {
    const { value } = e.target;
    setPatientLastName(value);
  };

  const handleOnEditPatientSSN1 = (e) => {
    const { value } = e.target;
    setPatientSSN1(value);
  };

  const handleOnEditPatientSSN2 = (e) => {
    const { value } = e.target;
    setPatientSSN2(value);
  };

  const handleOnEditPatientSSN3 = (e) => {
    const { value } = e.target;
    setPatientSSN3(value);
  };

  const handleOnEditPatientDisplayName = (e) => {
    const { value } = e.target;
    setPatientDisplayName(value);
  };

  const handleOnEditPatientUserName = (e) => {
    const { value } = e.target;
    setPatientUserName(value);
  };

  const handleOnEditPatientHomeStreetAddress = (e) => {
    const { value } = e.target;
    setPatientHomeStreetAddress(value);
  };

  const handleOnEditPatientHomeSuite = (e) => {
    const { value } = e.target;
    setPatientHomeSuite(value);
  };

  const handleOnEditPatientHomeCity = (e) => {
    const { value } = e.target;
    setPatientHomeCity(value);
  };

  const handleOnEditPatientHomeState = (e) => {
    const { value } = e.target;
    setPatientHomeState(value);
  };

  const handleOnEditPatientHomeZip = (e) => {
    const { value } = e.target;
    setPatientHomeZip(value);
  };

  const handleOnEditPatientMailingStreetAddress = (e) => {
    const { value } = e.target;
    setPatientMailingStreetAddress(value);
  };

  const handleOnEditPatientMailingSuite = (e) => {
    const { value } = e.target;
    setPatientMailingSuite(value);
  };

  const handleOnEditPatientMailingCity = (e) => {
    const { value } = e.target;
    setPatientMailingCity(value);
  };

  const handleOnEditPatientMailingState = (e) => {
    const { value } = e.target;
    setPatientMailingState(value);
  };

  const handleOnEditPatientMailingZip = (e) => {
    const { value } = e.target;
    setPatientMailingZip(value);
  };

  const handleOnChangePatientContactType = (val) => {
    setPatientContactType(val);
  };

  const handleOnEditPatientMobile = (e) => {
    const { value } = e.target;
    setPatientMobile(value);
  };

  const handleOnEditPatientEmail = (e) => {
    const { value } = e.target;
    setPatientEmail(value);
  };

  const handleOnClickSavePatientProfile = () => {
    // Patient Data
    onSavePatientProfile({
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientDobYear,
      patientDobDate,
      patientDobMonth,
      // dob: `${patientDobYear}-${padZero(patientDobMonth + 1)}-${padZero(patientDobDate)}` + 'T00:00:00.000Z',
      patientSSN1,
      patientSSN2,
      patientSSN3,
      patientAvatarImageUrl,
      patientDisplayName, // isPatientDisplayNameValid, isPatientDisplayNamePristine
      patientUserName, // isPatientUserNameValid, isPatientUserNamePristine, isPatientUserNameDuplicate
      patientHomeStreetAddress,
      patientHomeSuite,
      patientHomeCity,
      patientHomeState,
      patientHomeZip,
      mailingAddressSameAsHomeAddress: isPatientMailingAddressSameAsHomeAddress,
      patientMailingStreetAddress,
      patientMailingSuite,
      patientMailingCity,
      patientMailingState,
      patientMailingZip,
      contactType: patientContactType,
      mobile: patientMobile,
      email: patientEmail,
      patientIdFrontScan,
      patientIdBackScan
    });
    props.onCloseWithoutWarning();
  };

  // #endregion

  return (
    <Drawer
      placement={placement}
      style={{ ...style }}
      visible={isVisible}
      toggleDrawer={onClose}
      zIndex="2000"
      showMask={false}
      width={window.innerWidth <= 700 ? window.innerWidth : 375}
    >
      <div className={css(styles.sideDrawerContainer)}>
        {/* Header */}
        <div className={css(styles.sideDrawerHeader)}>
          <div className='fx fx__hv-center'>
            <i className='mr-2 fa fa-lg fa-user' />
            <div>Edit Patient Profile</div>
          </div>
          <i
            className={`fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
            onClick={onClose}
          />
        </div>
        {/* Content */}
        <div className={css(styles.sideDrawerContentArea)}>
          {/* Patient First, Middle and Last Name */}
          <div className='fx'>
            {/* First Name */}
            <div>
              <TextInputWithLabel
                value={patientFirstName}
                titleText="First Name"
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={115}
                onChange={handleOnEditPatientFirstName}
              />
            </div>
            {/* Middle Name */}
            <div className='ml-2'>
              <TextInputWithLabel
                value={patientMiddleName}
                titleText="Middle Name"
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={113}
                onChange={handleOnEditPatientMiddleName}
              />
            </div>
            {/* Last Name */}
            <div className='ml-2'>
              <TextInputWithLabel
                value={patientLastName}
                titleText="Last Name"
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={114}
                onChange={handleOnEditPatientLastName}
              />
            </div>
          </div>
          {/* Patient SSN and DOB */}
          <div className='mt-4'>
            <div className={css(styles.sectionHeading)}>SS#</div>
            <div
              className='fx'
              style={{
                alignItems: 'flex-end',
                marginTop: '-10px'
              }}
            >
              <div className='fx'>
                <TextInputWithLabel
                  value={patientSSN1}
                  titleText=''
                  placeholder='???'
                  titleFontSize={12}
                  fontSize={12}
                  isCompactLayout={false}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={26}
                  onChange={handleOnEditPatientSSN1}
                />
                <div className={css(styles.ssnSeparator)}>-</div>
                <TextInputWithLabel
                  value={patientSSN2}
                  titleText=''
                  placeholder='??'
                  titleFontSize={12}
                  fontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={26}
                  onChange={handleOnEditPatientSSN2}
                />
                <div className={css(styles.ssnSeparator)}>-</div>
                <TextInputWithLabel
                  value={patientSSN3}
                  titleText=''
                  placeholder='????'
                  titleFontSize={12}
                  fontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={35}
                  onChange={handleOnEditPatientSSN3}
                />
              </div>
              {/* DOB */}
              <div className='fx' style={{ marginLeft: '40px' }}>
                <i className='mr-2 fa fa-calendar' style={{ marginTop: '2px' }} />
                <div className={`pr-2 ${css(styles.sectionHeading)}`}>
                  Date of Birth
                </div>
                <DatePicker
                  left={0}
                  right={-10}
                  date={patientDobDate}
                  month={patientDobMonth}
                  year={patientDobYear}
                  isPrisitine={isPatientDobPristine}
                  color={showPatientDobAlert ? salmon[500] : blue[400]}
                  noUnderline
                  onChangeDate={(year, month, date) => {
                    setShowPatientDobAlert(false);
                    setPatientDobDate(date);
                    setPatientDobMonth(month);
                    setPatientDobYear(year);
                    setIsPatientDobPristine(false);
                  }}
                />
              </div>
            </div>
          </div>
          {/* Patient card */}
          <div className="fx mt-4">
            {/* Avatar */}
            <div className={css(styles.sideDrawerUserCardAvatar)}>
              <img
                src={patientAvatarImageUrl}
                ref={drawerAvatarRef}
                alt="avatar"
                className={css(styles.sideDrawerUserCardAvatarImg)}
                onClick={onClickAvatarCamera}
              />
              <div
                onClick={onClickAvatarCamera}
                className={css(styles.sideDrawerAvatarCamera)}
              >
                <i className="fa fa-camera" />
              </div>
            </div>
            {/* User card displayName & userName */}
            <div className={`ml-2 ${css(styles.sideDrawerUserCardText)}`}>
              {/* Display Name */}
              <div style={{ marginTop: '-16px' }}>
                <TextInputWithLabel
                  value={patientDisplayName}
                  titleText="Display Name (2 to 40 characters)*"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={268}
                  onChange={handleOnEditPatientDisplayName}
                />
                {/* Error message */}
                {!isPatientDisplayNameValid && !isPatientDisplayNamePristine
                  ? <div className={css(styles.errMsg)}>
                    Invalid Display Name
                  </div>
                  : null
                }
              </div>
              {/* UserName */}
              <div
                style={{
                  marginTop: (!isPatientDisplayNameValid && !isPatientDisplayNamePristine) ? '0px' : '4px'
                }}
              >
                <TextInputWithLabel
                  value={patientUserName}
                  titleText="Username (2 to 20 characters, no spaces) *"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor={blue[500]}
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={268}
                  onChange={handleOnEditPatientUserName}
                />
                {/* Error message */}
                {!isPatientUserNameValid && !isPatientUserNamePristine
                  ? <div className={css(styles.errMsg)}>
                    Invalid User Name
                  </div>
                  : null
                }
                {isPatientUserNameValid && !isPatientUserNamePristine && isPatientUserNameDuplicate
                  ? <div className={css(styles.errMsg)}>
                    User Name Taken
                  </div>
                  : null
                }
              </div>
            </div>
          </div>
          {/* Home Address */}
          <div className='mt-4'>
            {/* Heading */}
            <div className="fx fx__v-center" style={{ color: '#96c8ff' }}>
              <i className="fa fa-home" style={{ fontSize: '18px' }} />
              <div className="ml-2" style={{ fontSize: '14px', fontWeight: 'bold' }} >
                Home Address
              </div>
            </div>
            {/* Address */}
            <div className="fx">
              <div>
                <TextInputWithLabel
                  value={patientHomeStreetAddress}
                  titleText="Street"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={304}
                  placeholder='Required'
                  onChange={handleOnEditPatientHomeStreetAddress}
                />
              </div>
              <div className="ml-2">
                <TextInputWithLabel
                  value={patientHomeSuite}
                  titleText="Suite"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={45}
                  onChange={handleOnEditPatientHomeSuite}
                />
              </div>
            </div>
            {/* City, State and Zip */}
            <div className="fx mt-2">
              <div>
                <TextInputWithLabel
                  value={patientHomeCity}
                  titleText="City"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={206}
                  placeholder='Required'
                  onChange={handleOnEditPatientHomeCity}
                />
              </div>
              <div className="ml-2">
                <TextInputWithLabel
                  value={patientHomeState}
                  titleText="State"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={65}
                  placeholder='Required'
                  onChange={handleOnEditPatientHomeState}
                />
              </div>
              <div className="ml-2">
                <TextInputWithLabel
                  value={patientHomeZip}
                  titleText="Zip"
                  titleFontSize={12}
                  isCompactLayout={true}
                  titleColor={gray[200]}
                  textColor="white"
                  selectionColor="white"
                  selectionBackground="#0080FF"
                  spellcheck={false}
                  width={68}
                  placeholder='Required'
                  onChange={handleOnEditPatientHomeZip}
                />
              </div>
            </div>
          </div>
          {/* Mailing Address */}
          <div className='mt-4'>
            {/* Heading */}
            <div className="fx fx__v-center" style={{ color: '#96c8ff' }} >
              <i className="fa fa-envelope" style={{ fontSize: '18px' }} />
              <div className="ml-2" style={{ fontSize: '14px', fontWeight: 'bold' }} >
                Mailing Address
              </div>
              <div style={{ marginLeft: '50px', fontSize: '14px', color: '#96C8FF' }} >
                <Checkbox
                  text='Same as Home Address'
                  isChecked={isPatientMailingAddressSameAsHomeAddress}
                  onChange={() => {
                    setIsPatientMailingAddressSameAsHomeAddress(!isPatientMailingAddressSameAsHomeAddress);
                  }}
                />
              </div>
            </div>
            {/* Address */}
            {!isPatientMailingAddressSameAsHomeAddress &&
              <div className="fx">
                <div>
                  <TextInputWithLabel
                    value={patientMailingStreetAddress}
                    titleText="Street"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={304}
                    placeholder='Required'
                    onChange={handleOnEditPatientMailingStreetAddress}
                  />
                </div>
                <div className="ml-2">
                  <TextInputWithLabel
                    value={patientMailingSuite}
                    titleText="Suite"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={45}
                    onChange={handleOnEditPatientMailingSuite}
                  />
                </div>
              </div>
            }
            {/* City, State and Zip */}
            {!isPatientMailingAddressSameAsHomeAddress &&
              <div className="fx mt-2">
                <div>
                  <TextInputWithLabel
                    value={patientMailingCity}
                    titleText="City"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={206}
                    placeholder='Required'
                    onChange={handleOnEditPatientMailingCity}
                  />
                </div>
                <div className="ml-2">
                  <TextInputWithLabel
                    value={patientMailingState}
                    titleText="State"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={65}
                    placeholder='Required'
                    onChange={handleOnEditPatientMailingState}
                  />
                </div>
                <div className="ml-2">
                  <TextInputWithLabel
                    value={patientMailingZip}
                    titleText="Zip"
                    titleFontSize={12}
                    isCompactLayout={true}
                    titleColor={gray[200]}
                    textColor="white"
                    selectionColor="white"
                    selectionBackground="#0080FF"
                    spellcheck={false}
                    width={68}
                    placeholder='Required'
                    onChange={handleOnEditPatientMailingZip}
                  />
                </div>
              </div>
            }
          </div>
          {/* Contact # (Mobile or Phone) */}
          <div
            className='fx mt-4'
            style={{
              position: 'relative',
              padding: '0'
            }}
          >
            <div>
              <TextInputWithLabel
                value={patientMobile}
                titleText="Contact #"
                isCompactLayout={false}
                showPreIcon
                preIcon={`fa fa-lg ${patientContactType.toLowerCase() === 'mobile' ? 'fa-mobile' : 'fa-phone'}`}
                titleColor="#60A0E0"
                textColor="#0080FF"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={174}
                placeholder='Required'
                onChange={handleOnEditPatientMobile}
              />
            </div>
            <div
              style={{
                fontSize: '12px',
                position: 'relative',
                left: '-90px',
                color: blue[500]
              }}
              ref={contactRef}
              onClick={() => setIsContactMenuOpen(true)}
            >
              {patientContactType.toLowerCase() === 'mobile' ? 'Mobile' : 'Landline'}&nbsp;&nbsp;<i style={{ fontSize: '14px' }} className='fa fa-caret-down' />
            </div>
            <PopupMenu
              top={20}
              left={84}
              visible={isContactMenuOpen}
              parent={contactRef}
              position="absolute"
              onOutsideClick={() => { setIsContactMenuOpen(false); }}
            >
              <div
                className={css(styles.popupMenuContainer)}
                style={{ width: '110px' }}
              >
                {/* Mobile */}
                <div
                  className={`${patientContactType === 'mobile' ? 'selected' : ''} ${css(styles.popupMenuItemSmall, patientContactType === 'mobile' && styles.selectedPopupMenuItemSmall)}`}
                  onClick={() => {
                    handleOnChangePatientContactType('mobile');
                    setIsContactMenuOpen(false);
                  }}
                >
                  <div className={css(styles.menuItemIconContainerSmall)}>
                    <i className={`fa fa-mobile ${css(styles.menuItemIconSmall)}`} />
                  </div>
                  <div>Mobile</div>
                </div>
                {/* Landline */}
                <div
                  className={`${patientContactType === 'landline' ? 'selected' : ''} ${css(styles.popupMenuItemSmall, patientContactType === 'landline' && styles.selectedPopupMenuItemSmall)}`}
                  onClick={() => {
                    handleOnChangePatientContactType('landline');
                    setIsContactMenuOpen(false);
                  }}
                >
                  <div className={css(styles.menuItemIconContainerSmall)}>
                    <i className={`fa fa-phone ${css(styles.menuItemIconSmall)}`} />
                  </div>
                  <div>Landline</div>
                </div>
              </div>
            </PopupMenu>
          </div>
          {/* Email */}
          <div className='mt-4'>
            <TextInputWithLabel
              value={patientEmail}
              titleText="Email"
              isCompactLayout={false}
              showPreIcon
              preIcon='fa fa-envelope'
              titleColor="#60A0E0"
              textColor="#0080FF"
              selectionColor="white"
              selectionBackground="#0080FF"
              spellcheck={false}
              width={356}
              placeholder='Required'
              onChange={handleOnEditPatientEmail}
            />
          </div>
          {/* Patient ID - Front */}
          <div className='mt-4'>
            <div className='fx'>
              {idIcon}
              <div className='ml-2'>
                Patient ID (Front)
              </div>
            </div>
            {showFrontIdLoader
              ? <Loader iconType="cog" iconSize="5x" text="Uploading Patient ID(Front)" />
              : <div style={{ position: 'relative' }}>
                <img
                  className='mt-2'
                  onClick={onClickFrontIdCamera}
                  ref={frontCardRef}
                  src={patientFrontIdImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'}
                  style={{
                    width: '343px',
                    maxWidth: '343px',
                    height: 'auto'
                  }}
                />
                {/* Camera Icon */}
                <div
                  onClick={onClickFrontIdCamera}
                  className={css(styles.drawerBannerCameraIcon)}
                >
                  <i className="fa fa-camera" />
                </div>
              </div>
            }
          </div>
          {/* Patient ID - Back */}
          <div className='mt-4 mb-2'>
            <div className='fx'>
              {idIcon}
              <div className='ml-2'>
                Patient ID (Back)
              </div>
            </div>
            {showBackIdLoader
              ? <Loader iconType="cog" iconSize="5x" text="Uploading Patient ID(Back)" />
              : <div style={{ position: 'relative' }}>
                <img
                  className='mt-2'
                  ref={backCardRef}
                  src={patientBackIdImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'}
                  style={{
                    width: '343px',
                    maxWidth: '343px',
                    height: 'auto'
                  }}
                />
                <div
                  onClick={onClickBackIdCamera}
                  className={css(styles.drawerBannerCameraIcon)}
                >
                  <i className="fa fa-camera" />
                </div>
              </div>
            }
          </div>
          <div style={{ height: '40px' }}/>
        </div>
        {/* Footer */}
        <div className={css(styles.sideDrawerFooter)}>
          {/* Cancel Button */}
          <Button
            btnType="primary"
            className="fx fx__hv-center"
            isOutlineBtn
            onClick={onClose}
            style={{
              height: '32px',
              width: '85px',
              padding: '3px 0'
            }}
          >
            Cancel
          </Button>
          {/* Accept Button */}
          <Button
            btnType="primary"
            isDisabled={!canUpdatePatient || showFrontIdLoader || showBackIdLoader}
            className="ml-2 fx fx__hv-center"
            onClick={handleOnClickSavePatientProfile}
            style={{
              height: '32px',
              width: '85px',
              padding: '3px 0'
            }}
          >
            Accept
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default PatientProfileDrawer;
